const _ = require('lodash');
const ViewRenderer = require('client/src/view-renderer');
const log = require('core/src/log').instance("function/mapviewrenderer");
const {checkType} = require('utils/src/validation');
const Leaflet = require('leaflet');
const EsriLeaflet = require('esri-leaflet');
const circleToPolygon = require('circle-to-polygon');
const proj4 = require('proj4').default;
const leafletPip = require('@mapbox/leaflet-pip');
const {isTrue} = require('core/src/utils/validation');
const LeafletImage = require('leaflet-image');
const LeafletCurve = require('@elfalem/leaflet-curve');

require('proj4leaflet');
require('leaflet-draw');
require('leaflet-measure');
require('leaflet.markercluster/src/index');

require('leaflet/dist/leaflet.css');
require('leaflet-measure/dist/leaflet-measure.css');
require('leaflet-draw/dist/leaflet.draw.css');
require('leaflet.markercluster/dist/MarkerCluster.css');
require('leaflet.markercluster/dist/MarkerCluster.Default.css');

const LeafletMapViewRenderer = function(dependencies) {
	ViewRenderer.call(this, dependencies);

	this.autoPanZoom = false; // set to true when panning or zooming is done by the app (not manually by user)
	this.drawPaths = [];
	this.editableLayer = null; // L.FeatureGroup used as featureGroup for leaflet-draw editing
	this.editableFeaturesLayer = null; // L.GeoJSON layer for editableFeatures, lives inside editableLayer
	this.geoJSONLayerGroup = null; // layer group for markers and geoJSON display features
	this.geoJSONStyle = {};
	this.geoJSONSelectedStyle = {};
	this.geoJSONBounds = null;
	this.featuresSelected = {};
	// Icons to be used by markers keyed by a unique name
	this.icons = {};
	this.layers = [];
	this.layerControl = null;
	this.leafletMarkers = {};

	this.markers = {};
	this.markerBounds = null;
	this.markersSelected = {};
	this.markerClusterLayer = null;

	this.model = null;

	this.spiderfiedClusters = [];
};
LeafletMapViewRenderer.viewType = 'LeafletMapView';
LeafletMapViewRenderer.prototype = Object.create(ViewRenderer.prototype);


LeafletMapViewRenderer.prototype.doRender = function(data) {

	this.model = data;

	this.markers = _.keyBy(data.markers, 'id');

	// Fix default icons URLs
	Leaflet.Icon.Default.prototype.options.iconUrl = 'images/map-marker-blue.png';
	Leaflet.Icon.Default.prototype.options.iconRetinaUrl = 'images/map-marker-blue-2x.png';
	Leaflet.Icon.Default.prototype.options.shadowUrl = 'images/marker-shadow.png';

	this.iconMarker = L.icon({
		iconUrl: 'images/map-marker-blue.png',
		iconRetinaUrl: 'images/map-marker-blue-2x.png',
		iconSize: [25, 40],
		iconAnchor: [12, 40],
		popupAnchor: [1, -34],
		shadowUrl: 'images/marker-shadow.png',
		shadowSize: [41, 41]
	});

	this.iconMarkerSelected = L.icon({
		iconUrl: 'images/map-marker-red.png',
		iconRetinaUrl: 'images/map-marker-red-2x.png',
		iconSize: [25, 40],
		iconAnchor: [12, 40],
		popupAnchor: [1, -34],
		shadowUrl: 'images/marker-shadow.png',
		shadowSize: [41, 41]
	});

	this.syncIcons(data.icons);

	let div = $('<div class="leaflet-map-view-renderer" style="width: 100%; height: 100%;">');

	this.autoPanZoom = true;

	this.projections = _.mapValues(
		data.projections, 
		(p, name) => {
			return new L.Proj.CRS(
				name,
				p.projection,
				p.options
			)
		}
	)

	this.mapCRS = Leaflet.CRS[data.options.crs] || this.projections[data.options.crs] || Leaflet.CRS.EPSG3857;

	// Prevent zoom on boxzoom when map.noFit = true
	Leaflet.Map.BoxZoom.prototype._onMouseUp = function (e) {
		if ((e.which !== 1) && (e.button !== 1)) { return; }

		this._finish();

		if (!this._moved) { return; }
		// Postpone to next JS tick so internal click event handling
		// still see it as "moved".
		this._clearDeferredResetState();
		this._resetStateTimeout = setTimeout(L.Util.bind(this._resetState, this), 0);

		var bounds = new L.LatLngBounds(
			this._map.containerPointToLatLng(this._startPoint),
			this._map.containerPointToLatLng(this._point)
		);
		this._map.fire('boxzoomend', {boxZoomBounds: bounds})

		if (!this._map.options.noFit) {
			this._map.fitBounds(bounds);
		}
	};

	this.map = Leaflet.map(
		div[0], 
		_.extend(
			{
				center: data.options.center || [data.center.lat || 0, data.center.lng || data.center.long || 0],
				zoom: data.options.zoom || data.zoom,
				noFit: true,
				preferCanvas: true // required for leaflet-image to output the GeoJSON correctly
			}, 
			data.options,
			{
				crs: this.mapCRS
			}
		)
	)

	this.editableLayer = Leaflet.featureGroup();
	// leaflet-draw calls eachLayer to reach leaf layers (L.Polygon, L.Polyline, etc.) for editing.
	// editableFeaturesLayer is an L.GeoJSON group nested inside editableLayer, and L.GeoJSON has no
	// .editing property, so we flatten one level deep so draw can find the actual shape layers.
	const origEachLayer = this.editableLayer.eachLayer.bind(this.editableLayer);
	this.editableLayer.eachLayer = function(fn, context) {
		origEachLayer(layer => {
			if (typeof layer.eachLayer === 'function') {
				layer.eachLayer(fn, context);
			} else {
				fn.call(context, layer);
			}
		});
		return this;
	};
	this.map.addLayer(this.editableLayer);

	this.setupLayers(data);

	this.setupDrawControl(data);

	if (isTrue(data.markerCluster.enabled)) {
		let markerClusterOptions = _.get(data, 'markerCluster.options');

		// We need Leaflet instance as a parameter for iconCreateFunction so we are calling the given function with the additional paramter
		if (_.isFunction(_.get(markerClusterOptions, 'iconCreateFunction'))) {
			let oldIconCreateFunction = markerClusterOptions.iconCreateFunction;
			markerClusterOptions = _.extend({}, markerClusterOptions, {
				iconCreateFunction: (cluster) => oldIconCreateFunction(cluster, Leaflet)
			});
		}

		this.markerClusterLayer = L.markerClusterGroup(markerClusterOptions);

		if (data.markerCluster.multipleSpiderfiedClusters) {
			
			this.markerClusterLayer.on('clusterclick spiderfied unspiderfied',
				(ev) => {
					_.map(
						this.drawPaths,
						(l) => {
							this.map.removeLayer(l.layer)
						}
					)

					let timeout;
					let onAnimationEnd = () => {
						_.map(
							this.drawPaths,
							(l) => {
								this.map.addLayer(l.layer)
							}
						)

						this.markerClusterLayer.off('animationend', onAnimationEnd);
						clearTimeout(timeout);
					}

					this.markerClusterLayer.on('animationend', onAnimationEnd);

					// ensure the paths are shown again if there is no animation
					timeout = setTimeout(
						onAnimationEnd,
						1000
					)
				}
			);

			this.spiderfyCluster = (layer) => {
				var childMarkers = layer.getAllChildMarkers(null, true),
					center = this.map.latLngToLayerPoint(layer._latlng),
					positions;

				if (layer._group.options.spiderfyShapePositions) {
					positions = layer._group.options.spiderfyShapePositions(childMarkers.length, center);
				} else if (childMarkers.length >= layer._circleSpiralSwitchover) {
					positions = layer._generatePointsSpiral(childMarkers.length, center);
				} else {
					center.y += 10; // Otherwise circles look wrong => hack for standard blue icon, renders differently for other icons.
					positions = layer._generatePointsCircle(childMarkers.length, center);
				}

				layer._animationSpiderfy(childMarkers, positions);
			}

			this.markerClusterLayer.on('clusterclick',
				(ev) => {
					if (_.includes(this.spiderfiedClusters, ev.layer)) {
						ev.layer.unspiderfy();
						_.pull(this.spiderfiedClusters, ev.layer);
					}
					else {
						this.spiderfyCluster(ev.layer);
						this.spiderfiedClusters.push(ev.layer);
					}
				}
			);

			this.map.on('zoomstart',
				(ev) => {
					_.forEach(
						this.spiderfiedClusters,
						(layer) => {
							layer._noanimationUnspiderfy();
						}
					)
					this.spiderfiedClusters = [];
				}
			)
		}

		this.markerClusterLayer.on('spiderfied',
			(ev) => {
				this.userEvent({
					type: 'clusterExpanded',
					markers: _.map(
						ev.markers,
						(m) => {
							return _.extend(
								{},
								_.get(this.markers, m.options.id),
								{
									visLat: m._latlng.lat,
									visLng: m._latlng.lng
								}
							)
						}
					)
				})
			}
		);

		this.markerClusterLayer.on('unspiderfied',
			(ev) => {
				this.userEvent({
					type: 'clusterCollapsed',
					markers: _.map(
						ev.markers,
						(m) => {
							return _.extend(
								{},
								_.get(this.markers, m.options.id),
								{
									visLat: m._latlng.lat,
									visLng: m._latlng.lng
								}
							)
						}
					)
				})

			}
		)

		let debouncedOnClustesUpdated = _.debounce((ev) => {
			if (_.isEmpty(this.markers)) {
				return;
			} 

			this.userEvent({
				type: 'clustersUpdated',
				markers: _.map(
					this.markers,
					(m) => {
						let leafletMarker = _.get(this.leafletMarkers, m.id);
						let visibleParent = this.markerClusterLayer.getVisibleParent(leafletMarker);
						let vLatLng = ! _.isNil(visibleParent) ? visibleParent.getLatLng() : leafletMarker.getLatLng();

						return _.extend(
							{},
							m,
							{
								visLat: vLatLng.lat,
								visLng: vLatLng.lng
							}
						)
					}
				)
			})
		})

		this.markerClusterLayer.on('animationend layeradd layerremove',
			(ev) => {
				debouncedOnClustesUpdated(ev);
			}
		)
		this.geoJSONLayerGroup = this.markerClusterLayer;
	}
	else {
		this.geoJSONLayerGroup = L.layerGroup();
	}

	this.map.addLayer(this.geoJSONLayerGroup);

	if (isTrue(data.controls.measure.enabled)) {
		L.Control.Measure.include({
			_setCaptureMarkerIcon: function () {
				this._captureMarker.options.autoPanOnFocus = false;
				this._captureMarker.setIcon(
					L.divIcon({
						iconSize: this._map.getSize().multiplyBy(2)
					})
				);
			},
		});

		this.measureControl = L.control.measure(data.controls.measure);
		this.measureControl.options.units['sqkilometers'] = {
			factor: 0.000001,
			display: 'sqkilometers',
			decimals: 3
		};
		
		this.map.addControl(this.measureControl);
	}

	// Create markers
	_.forEach(data.markers, (marker) => this.markerAdd(marker));

	if (! _.isEmpty(data.markers)) {
		this.markerBounds = Leaflet.latLngBounds(_.map(data.markers, (m) => Leaflet.latLng(m.lat, m.lng || m.long)));
	}

	// Create geoJSON — features with _editing:true are routed to the editable layer
	this.geoJSONStyle = data.geoJSONStyle;
	this.geoJSONSelectedStyle = data.geoJSONSelectedStyle;
	this.geoJSONFilter = data.geoJSONFilter;
	const geoJSON = this._splitGeoJSON(data.geoJSON);

	if (! _.isNil(data.geoJSON)) {
		this.resetGeoJSONLayer();
		this.geoJSONLayer.addData({ type: 'FeatureCollection', features: geoJSON.toDisplay || []});
		this.geoJSONBounds = this.geoJSONLayer.getBounds();
		if (! this.geoJSONBounds.isValid()) {
			this.geoJSONBounds = null;
		}
	}

	// Create editable features layer (always, so the draw control has a valid featureGroup)
	this.resetEditableFeaturesLayer();
	if (! _.isEmpty(geoJSON.toEdit)) {
		this.editableFeaturesLayer.addData({ type: 'FeatureCollection', features: geoJSON.toEdit });
		if (this.drawControl) {
			_.defer(() => {
				const editHandler = _.get(this.drawControl, '_toolbars.edit._modes.edit.handler');
				if (editHandler) editHandler.enable();
			});
		}
	}

	if (data.autoFit) {
		_.defer(() => this.zoomToFit())
	}

	// Add drawings
	this.syncDrawPaths(data.drawPaths);

	if (data.bounds && _.size(data.bounds) === 2) {
		setTimeout(() => this.fitBounds(data.bounds), 500)
	}
	// Set up events
	this.map.on(
		'click',
		(ev) => {
			this.userEvent({
				type: 'mapClick',
				location: ev.latlng,
				x: ev.originalEvent.pageX,
				y: ev.originalEvent.pageY,
				keyPressed: {
					alt: _.get(ev, 'originalEvent.altKey', false),
					ctrl:  _.get(ev, 'originalEvent.ctrlKey', false),
					meta:  _.get(ev, 'originalEvent.metaKey', false),
					shift:  _.get(ev, 'originalEvent.shiftKey', false)
				},
				features: _.isNil(this.geoJSONLayer) 
					? [] 
					: leafletPip.pointInLayer(ev.latlng, this.geoJSONLayer).map(layer => layer.feature)
			})
		}
	);

	this.map.on(
		'contextmenu',
		(ev) => {
			this.userEvent({
				type: 'mapRightClick',
				location: ev.latlng,
				keyPressed: {
					alt: _.get(ev, 'originalEvent.altKey', false),
					ctrl:  _.get(ev, 'originalEvent.ctrlKey', false),
					meta:  _.get(ev, 'originalEvent.metaKey', false),
					shift:  _.get(ev, 'originalEvent.shiftKey', false)
				},
				x: ev.originalEvent.pageX,
				y: ev.originalEvent.pageY
			})

			let featuresUnderMouse = leafletPip.pointInLayer(ev.latlng, this.geoJSONLayer).map(layer => layer.feature);

			if (_.isEmpty(featuresUnderMouse)) {
				this.openContextMenu('map', {location: ev.latlng}, ev.originalEvent.pageX, ev.originalEvent.pageY);
			}

		}

	);

	this.map.on(
		'moveend',
		(ev) => {
			let bounds = this.map.getBounds();
			this.userEvent({
				type: 'boundsChanged',
				bounds: {
					east: bounds._northEast.lng,
					north: bounds._northEast.lat,
					south: bounds._southWest.lat,
					west: bounds._southWest.lng
				},
				center: this.map.getCenter(),
				zoom: this.map.getZoom(),
			})

			let modelUpdate = {
				bounds: {
					east: bounds._northEast.lng,
					north: bounds._northEast.lat,
					south: bounds._southWest.lat,
					west: bounds._southWest.lng
				}
			};

			// Only write center/zoom back to the model on user-initiated moves.
			// On programmatic moves (autoPanZoom) writing them back would re-trigger
			// doUpdate -> setView -> moveend, causing an infinite loop.
			if (! this.autoPanZoom) {
				modelUpdate.center = this.map.getCenter();
				modelUpdate.zoom = this.map.getZoom();
				modelUpdate.autoFit = false;
			}

			this.requestUpdate(modelUpdate);
		}
	);

	this.map.on(
		'boxzoomend',
		(ev) => {
			this.userEvent({
				type: 'envelope',
				keyPressed: {
					alt: _.get(ev, 'originalEvent.altKey', false),
					ctrl:  _.get(ev, 'originalEvent.ctrlKey', false),
					meta:  _.get(ev, 'originalEvent.metaKey', false),
					shift:  _.get(ev, 'originalEvent.shiftKey', false)
				},				
				east: ev.boxZoomBounds._northEast.lng,
				north: ev.boxZoomBounds._northEast.lat,
				south: ev.boxZoomBounds._southWest.lat,
				west: ev.boxZoomBounds._southWest.lng
			})
		}
	)

	this.map.on(
		Leaflet.Draw.Event.DRAWSTART, 
		(e) => {
			this.requestUpdate({
				'controls.drawing.isDrawing': true
			});
		}
	)

	this.map.on(
		Leaflet.Draw.Event.DRAWSTOP, 
		(e) => {
			// Wait for the callstack to clear before setting this (to prevent setting it before mapClick is triggered by drawing stoping)
			_.defer(
				() => this.requestUpdate({
					'controls.drawing.isDrawing': false
				})
			);
		}
	)

	this.map.on(
		Leaflet.Draw.Event.CREATED, 
		(e) => {
			let drawingGeoJSON = (e.layerType == 'circle') 
				? {
					type: 'Feature',
					geometry: circleToPolygon(
						[e.layer.getLatLng().lng, e.layer.getLatLng().lat], 
						e.layer.getRadius(),
						{numberOfEdges: 64}
					),
					properties: {}
				}
				: e.layer.toGeoJSON();
		
			this.userEvent({
				type: 'drawing',
				tool: e.layerType,
				geoJSON: drawingGeoJSON,
				keyPressed: {
					alt: _.get(e, 'originalEvent.altKey', false),
					ctrl:  _.get(e, 'originalEvent.ctrlKey', false),
					meta:  _.get(e, 'originalEvent.metaKey', false),
					shift:  _.get(e, 'originalEvent.shiftKey', false)
				},
			})				
		}
	);

	this.map.on(
		Leaflet.Draw.Event.EDITED,
		(e) => {
			let updatedFeatures = [];
			e.layers.eachLayer((layer) => {
				if (layer.feature) {
					// Preserve original feature (id, properties) with updated geometry
					let f = _.assign({}, layer.feature, { geometry: layer.toGeoJSON().geometry});
					delete f._editing;
					updatedFeatures.push(f);
				}
			});

			if (_.isEmpty(updatedFeatures)) {
				return;
			}

			this.requestUpdate({ '_update.set.geoJSON.features': updatedFeatures });

			this.userEvent({
				type: 'featuresEdited',
				features: updatedFeatures
			});
		}
	);

	this.map.on(
		Leaflet.Draw.Event.EDITSTOP,
		(e) => {
			let updatedFeatures = [];
			_.forEach(
				_.get(this.model, 'geoJSON.features', []),
				(feature) => {
					if (feature._editing) {
						updatedFeatures.push({id: feature.id, _editing: false})
					}
				}
			)

			if (! _.isEmpty(updatedFeatures)) {
				this.requestUpdate({ '_update.merge.geoJSON.features': updatedFeatures });
			}
		}
	);

	this.map.on('measurefinish',
		(results) => {
			this.userEvent({
				type: 'measure',
				area: results.area,
				areaDisplay: results.areaDisplay,
				lastCoord: results.lastCoord,
				length: results.length,
				lengthDisplay: results.lengthDisplay,
				pointCount: results.pointCount,
				points: results.points
			})
		}
	);

	this.map.on('baselayerchange',
		(params) => {
			let center = this.map.getCenter();
			this.map.options.crs = this.projections[params.layer.options.crs] || this.mapCRS;
			this.map.setView(center);
			this.map._resetView(this.map.getCenter(), this.map.getZoom());

			this.requestUpdate({
				layers: _.extend(
					{_valueObject: true}, 
					_.mapValues(
						this.model.layers,
						(layer, key) => _.extend({}, layer, {
							active: this.map.hasLayer(this.layers[key])
						})
					)
				)
			});

			this.userEvent({
				type: 'baseLayerChanged',
				key: _.find(this.model.layerControl.baselayers, (label, key) => key == params.name),
				label: params.name
			})					

		}
	);



	let eventHandlerAssigned = false;
	const handleInfoClick = (el) => {

		this.userEvent({
			type: 'infoClick',
			data: el.currentTarget.dataset
		});
	}
	this.map.on(
		'popupopen',
		() => {
			if (!eventHandlerAssigned && document.querySelector('.leaflet-popup-content button.trigger-click, .leaflet-popup-content a.trigger-click')){
				// Remove previous event listener and add a new one
				document.querySelectorAll('.leaflet-popup-content .trigger-click').forEach(function(el) {
					el.removeEventListener('click', handleInfoClick); // Remove previous listener
					el.addEventListener('click', handleInfoClick);    // Add new listener
				});
				eventHandlerAssigned = true
			}
		}

	);
		
	// Reset eventHandlerAssigned flag when the popup is closed
	this.map.on(
		'popupclose',
		() => {
			eventHandlerAssigned = false;
		}
	);

	this.autoPanZoom = false;

	if (data.toImage) {
		this.requestUpdate({toImage: false}).then(() => {
			this.mapToImage();
		});
	}

	return div;
};

LeafletMapViewRenderer.prototype.setView = function(center, zoom) {
	this.autoPanZoom = true;
	this.map.once('moveend', () => {
		this.autoPanZoom = false;
	});
	this.map.setView(
		{
			lat: center.lat,
			lng: center.lng || center.long
		},
		zoom
	);
};

LeafletMapViewRenderer.prototype.doUpdate = function(data, changes, updateId) {
	this.model = data;

	if (this.hasChangedIn(changes, 'icons')) {
		this.syncIcons(data.icons);
	}

	if (this.hasChangedIn(changes, 'layers') || this.hasChangedIn(changes, 'layerControl')) {
		this.setupLayers(data);
	}

	// Triggering setupDrawControl cancels drawing so we want to avoid that when the status changes
	if (this.hasChangedIn(_.omit(changes, 'controls.drawing.isDrawing'), 'controls.drawing')) {
		this.setupDrawControl(data);
	}
	
	if (this.hasChangedIn(changes, 'center') || this.hasChangedIn(changes, 'zoom')) {
		const isOwnEcho = ! _.isNil(updateId) && (updateId in this._updateRequests);
		if (! isOwnEcho) {
			this.setView(data.center, data.zoom);
		}
	}

	// Update markers add, remove, update
	if (! _.isNil(changes.markers)) {

		if (! _.isEmpty(this.spiderfiedClusters)) {
			_.forEach(
				this.spiderfiedClusters,
				(layer) => {
					layer._noanimationUnspiderfy();
				}
			);
			this.spiderfiedClusters = [];
		}

		let oldMarkers = _.keyBy(changes.markers.old, 'id');
		let newMarkers = _.keyBy(changes.markers.new, 'id');

		let markersTo = _.groupBy(
			changes.markers.new,
			(marker) => {
				if (_.isNil(oldMarkers[marker.id])) {
					return 'add';
				}

				if (_.isEqual(marker, oldMarkers[marker.id])) {
					return 'same';
				}

				return 'update';
			}
		);

		markersTo.remove = _.reduce(
			oldMarkers,
			(res, marker) => (_.isNil(newMarkers[marker.id]) ? (res.push(marker), res) : res),
			[]
		);

		_.forEach(markersTo.remove, (m) => this.markerRemove(m));
		_.forEach(markersTo.update, (m) => this.markerUpdate(m));
		_.forEach(markersTo.add, (m) => this.markerAdd(m));

		this.markers = _.keyBy(data.markers, 'id');
	}

	if (this.hasChangedIn(changes, 'state.selected.markers')) {
		let selectedMarkersById = _.keyBy(data.state.selected.markers, 'id');

		// unselect markers
		_.forEach(
			this.markersSelected,
			(marker) => {
				if (! selectedMarkersById[marker.id]) {
					this.markerUnselect(marker);
				}
			}
		);

		// select markers
		_.forEach(
			data.state.selected.markers,
			(marker) => {
				this.markerSelect(marker);
			}
		);		
	}

	if (_.isEmpty(data.markers)) {
		this.markerBounds = null;
	}
	else {
		this.markerBounds = Leaflet.latLngBounds(_.map(data.markers, (m) => Leaflet.latLng(m.lat || 0, m.lng || m.long || 0)));
	}

	this.geoJSONStyle = data.geoJSONStyle;
	this.geoJSONSelectedStyle = data.geoJSONSelectedStyle;
	this.geoJSONFilter = data.geoJSONFilter;
	if (_.isEmpty(_.get(data, 'geoJSON.features'))) {
		this.geoJSONBounds = null;
	}
	if (! _.isNil(changes.geoJSON) || ! _.isNil(changes['geoJSON.features']) || ! _.isNil(changes.geoJSONFilter)) {
		const editHandler = _.get(this.drawControl, '_toolbars.edit._modes.edit.handler');
		if (editHandler && editHandler._enabled) {
			editHandler.disable();
		}

		const geoJSON = this._splitGeoJSON(data.geoJSON);


		this.resetGeoJSONLayer();
		if (! _.isNil(data.geoJSON)) {
			this.geoJSONLayer.addData({type: 'FeatureCollection', features: geoJSON.toDisplay || []});
			this.geoJSONBounds = this.geoJSONLayer.getBounds();
			if (! this.geoJSONBounds.isValid()) {
				this.geoJSONBounds = null;
			}
		}

		this.resetEditableFeaturesLayer();
		if (! _.isEmpty(geoJSON.toEdit)) {
			this.editableFeaturesLayer.addData({ type: 'FeatureCollection', features: geoJSON.toEdit });
			if (this.drawControl) {
				_.defer(() => {
					const handler = _.get(this.drawControl, '_toolbars.edit._modes.edit.handler');
					if (handler) handler.enable();
				});
			}
		}
	}

	if (this.measureControl) {
		// ensure measure layer is on top
		this.map.removeLayer(this.measureControl._layer);
		this.map.addLayer(this.measureControl._layer);

	}

	if (this.hasChangedIn(changes, 'drawPaths')) {
		this.syncDrawPaths(data.drawPaths);
	}

	if (data.autoFit  
		&& (
				! _.isNil(changes.autoFit) 
			|| 	! _.isNil(changes.markers) 
			|| 	! _.isNil(changes.geoJSON) 
			|| 	! _.isNil(changes['geoJSON.features'])
		)
	) {
		this.zoomToFit();
	}

	if (data.toImage) {
		this.requestUpdate({toImage: false}).then(() => {
			this.mapToImage();
		});
	}
};

LeafletMapViewRenderer.prototype.doResize = function() {
	this.autoPanZoom = true;
	this.map.invalidateSize();
	this.autoPanZoom = false;
}

/* --------------------  Setup  ---------------------*/

// Builds (or rebuilds) the leaflet-draw control from controls.drawing. leaflet-draw
// reads the draw/edit options only when L.Control.Draw is constructed, so to honor a
// changed config (e.g. controls.drawing.draw.polyline = false coming from a trigger)
// we tear down the existing control and create a fresh one with the new options.
LeafletMapViewRenderer.prototype.setupDrawControl = function(data) {
	if (this.drawControl) {
		this.map.removeControl(this.drawControl);
		this.drawControl = null;
	}

	if (isTrue(data.controls.drawing.enabled)) {
		let drawingOptions = _.cloneDeep(data.controls.drawing);
		if (drawingOptions.edit !== false) {
			drawingOptions.edit = _.assign({ featureGroup: this.editableLayer, remove: false }, drawingOptions.edit);
		}
		this.drawControl = new L.Control.Draw(drawingOptions);
		this.map.addControl(this.drawControl);
	}
};

LeafletMapViewRenderer.prototype.setupLayers = function(data) {

	// remove current layers
	_.map(
		this.layers,
		(layer) => {
			this.map.removeLayer(layer)
		}
	)

	// Create layers
	this.layers = _.mapValues(
		data.layers, 
		(layer) => {
			if (layer.type === 'tileLayer') {
				return Leaflet.tileLayer(
					layer.url,
					layer
				);
			}
			return layer;
		}
	)

	this.activeLayers = _.keys(_.pickBy(
		data.layers,
		'active'
	));

	let center = this.map.getCenter();
	_.forEach(this.activeLayers, (layerName) => {
		this.layers[layerName].addTo(this.map);
		this.map.options.crs = this.projections[this.layers[layerName].options.crs] || this.mapCRS;
	})
	this.map.setView(center);
	this.map._resetView(this.map.getCenter(), this.map.getZoom());

	if (this.layerControl)  {
		this.map.removeControl(this.layerControl);
	}

	// Create layer control
	if (_.size(data.layerControl)) {
		let baselayers = _.mapValues(data.layerControl.baselayers, (layerName) => this.layers[layerName]);
		let overlays = _.mapValues(data.layerControl.overlays, (layerName) => this.layers[layerName]);
		this.layerControl = L.control.layers(baselayers, overlays).addTo(this.map);
	}
}


/* --------------------   Map  ---------------------*/


LeafletMapViewRenderer.prototype.fitBounds = function(bounds) {
	this.autoPanZoom = true;
	bounds = Leaflet.latLngBounds(bounds);
	if (bounds.isValid())
		this.map.fitBounds(bounds);
}


LeafletMapViewRenderer.prototype.zoomToFit = function() {
	let mapBounds = this.markerBounds || this.geoJSONBounds;

	if (! _.isNil(mapBounds)) {
		mapBounds.extend(this.markerBounds).extend(this.geoJSONBounds)
	} 
	else {
		mapBounds = Leaflet.latLngBounds();
	} 

	if (! mapBounds.isValid()) {
		return;
	}

	this.preventBoundsChanged = true;
	this.map.setView(mapBounds.getCenter(), this.map.getMaxZoom());
	this.preventBoundsChanged = false;
	this.map.fitBounds(mapBounds);
}

LeafletMapViewRenderer.prototype.mapToImage = function() {
	LeafletImage(this.map, (err, canvas) => {
		if (! _.isNil(err)) {
			console.error(err);
			return;
		}

		let dimensions = this.map.getSize();
		this.userEvent({
			type: 'mapImage',
			imageData: canvas.toDataURL(),
			width: dimensions.x,
			height: dimensions.y
		})
	});		
}

/* --------------------   Markers  ---------------------*/


LeafletMapViewRenderer.prototype.markerAdd = function(marker) {
	let icon;

	if (_.isObject(marker.icon)) {
		icon = Leaflet.icon(marker.icon);
	}
	else if(_.isString(marker.icon)){
		icon = this.icons[marker.icon];
	}

	/* Commented out because 
		1. it should not be needed
		2. it's slow
	Replaced with just a markerRemove call
	
	let existingMarker;
	if (this.markerClusterLayer) {
		existingMarker = this.markerClusterLayer.getLayers().find(m => {
			return m.options.id === marker.id
		});
		this.leafletMarkers[marker.id] = existingMarker;
	} 
	else {
		existingMarker = _.find(this.map._layers, layer => {
			if (layer instanceof L.Marker) {
				const latLng = layer.getLatLng();
				let long = marker.lng || marker.long || 0;
	            // Check if the marker with the same position already exists
	            if (latLng.lat === marker.lat && latLng.lng === long) {
	            	this.leafletMarkers[marker.id] = layer;
	                return true; // Marker with this lat/lng exists
	            }
			}
		})
	}

	if (existingMarker)
		return;
	*/

	if (this.leafletMarkers[marker.id]) {
		this.markerRemove(marker);
	}

	this.leafletMarkers[marker.id] = Leaflet.marker(
		[marker.lat || 0, marker.lng || marker.long || 0], 
		_.extend({}, marker, (_.isNil(icon) ? {} : {icon: icon}))
	).addTo(this.geoJSONLayerGroup);

	this.leafletMarkers[marker.id].on(
		'click', 
		(ev) => {
			let markerId = ev.target.options.id;

			if (ev.originalEvent.ctrlKey) {
				Leaflet.DomEvent.stopPropagation(ev);
				this.markerSelectToggle(this.markers[markerId]);
			}
			else {
				this.userEvent({
					type: 'markerClick',
					keyPressed: {
						alt: _.get(ev, 'originalEvent.altKey', false),
						ctrl:  _.get(ev, 'originalEvent.ctrlKey', false),
						meta:  _.get(ev, 'originalEvent.metaKey', false),
						shift:  _.get(ev, 'originalEvent.shiftKey', false)
					},					
					marker: this.markers[markerId]
				});

				if (_.isString(this.markers[markerId].info) && ! _.isEmpty(this.markers[markerId].info)) {
					Leaflet.popup()
						.setLatLng(this.leafletMarkers[markerId].getLatLng())
						.setContent(this.markers[markerId].info)
						.openOn(this.map)
				}
			}
		}
	);

	this.leafletMarkers[marker.id].on(
		'contextmenu', 
		(ev) => {
			this.userEvent({
				type: 'markerRightClick',
				keyPressed: {
					alt: _.get(ev, 'originalEvent.altKey', false),
					ctrl:  _.get(ev, 'originalEvent.ctrlKey', false),
					meta:  _.get(ev, 'originalEvent.metaKey', false),
					shift:  _.get(ev, 'originalEvent.shiftKey', false)
				},									
				marker: this.markers[ev.target.options.id]
			});
			
			this.openContextMenu('marker', {
				location: ev.latlng, 
				marker: this.markers[ev.target.options.id], 
			}, ev.originalEvent.pageX, ev.originalEvent.pageY);
		}
	);
}

LeafletMapViewRenderer.prototype.markerUpdate = function(marker) {
	this.markerRemove(marker);
	this.markerAdd(marker);
}

LeafletMapViewRenderer.prototype.markerRemove = function(marker) {
	// To remove the marker from the markerClusterLayer or the map
	const markerToRemove = this.leafletMarkers[marker.id]; // Get the marker by its ID

	// Remove from the cluster layer (or map if it's not in a cluster)
	if (this.markerClusterLayer) {
	    this.markerClusterLayer.removeLayer(markerToRemove);
	} else {
	    this.map.removeLayer(markerToRemove); // Remove directly from the map if not in cluster layer
	}
	delete this.leafletMarkers[marker.id];
}

LeafletMapViewRenderer.prototype.markerSelect = function(marker) {
	if (this.markersSelected[marker.id]) {
		return false;
	}

	this.markersSelected[marker.id] = marker;
	this.leafletMarkers[marker.id].setIcon(this.iconMarkerSelected);

	return true;
}

LeafletMapViewRenderer.prototype.markerUnselect = function(marker) {
	if (! this.markersSelected[marker.id]) {
		return false;
	}

	delete this.markersSelected[marker.id];
	this.leafletMarkers[marker.id].setIcon(this.iconMarker);

	return true;
}

LeafletMapViewRenderer.prototype.markerSelectToggle = function(marker) {
	if (! this.markerSelect(marker)) {
		this.markerUnselect(marker);
	}

	let selectedMarkers = _.values(this.markersSelected);

	this.userEvent({
		type: 'selectionChanged',
		markers: selectedMarkers
	})

	this.requestUpdate({
		'state.selected.markers': selectedMarkers
	})
}


/* --------------------   GeoJSON  ---------------------*/


LeafletMapViewRenderer.prototype._splitGeoJSON = function(geoJSON) {
	return _.groupBy(
		_.get(geoJSON, 'features', []),
		(feature) => feature._editing ? 'toEdit' : 'toDisplay'
	)
};

LeafletMapViewRenderer.prototype.resetGeoJSONLayer = function() {
	if (! _.isNil(this.geoJSONLayer)) {
		this.geoJSONLayer.removeFrom(this.geoJSONLayerGroup);
	}



	this.geoJSONLayer = Leaflet.geoJSON(
		null,
		{
			filter: ( _.isFunction(this.geoJSONFilter) ? this.geoJSONFilter : undefined ),

			pointToLayer: (point, latLng) => {
				if (_.isFunction(this.model.geoJSONPointToLayer)) {
					let path = this.model.geoJSONPointToLayer(point, latLng, L);
					if (path) {
						return path;
					}
				};

				return L.marker(latLng);
			},

			style: (feature) => {
					if (this.featuresSelected[feature.id]) {
						return feature.selectedStyle || this.geoJSONSelectedStyle;
					}
					return feature.style || this.geoJSONStyle;
				},

			onEachFeature: (feature, layer) => {
				if (! _.isNil(feature.tooltip)) {
					layer.bindTooltip(
						(layer) => {
							return (_.isFunction(layer.feature.tooltip) 
								? layer.feature.tooltip(layer.feature) 
								: layer.feature.tooltip);
						}, 
						layer.feature.tooltipOptions
					);
				}


				layer.on(
					'click',
					(ev) => {
						if (ev.originalEvent.ctrlKey) {
							this.geoJSONFeatureSelectToggle(ev.target.feature);
						}
						else {							
							this.userEvent({
								type: 'featureClick',
								feature: feature,
								keyPressed: {
									alt: _.get(ev, 'originalEvent.altKey', false),
									ctrl:  _.get(ev, 'originalEvent.ctrlKey', false),
									meta:  _.get(ev, 'originalEvent.metaKey', false),
									shift:  _.get(ev, 'originalEvent.shiftKey', false)
								},													
								location: ev.latlng
							});
						}
					}
				)

				layer.on(
					'contextmenu',
					(ev) => {
						let featuresUnderMouse = leafletPip.pointInLayer(ev.latlng, this.geoJSONLayer).map(layer => layer.feature);

						this.userEvent({
							type: 'featureRightClick',
							feature: feature,
							features: featuresUnderMouse,
							location: ev.latlng,
							keyPressed: {
								alt: _.get(ev, 'originalEvent.altKey', false),
								ctrl:  _.get(ev, 'originalEvent.ctrlKey', false),
								meta:  _.get(ev, 'originalEvent.metaKey', false),
								shift:  _.get(ev, 'originalEvent.shiftKey', false)
							},
							x: ev.originalEvent.pageX,
							y: ev.originalEvent.pageY
						})

						this.openContextMenu('feature', {location: ev.latlng, feature, features: featuresUnderMouse}, ev.originalEvent.pageX, ev.originalEvent.pageY);

					}

				)
			}
		}
	);

	this.geoJSONLayerGroup.addLayer(this.geoJSONLayer);

}

LeafletMapViewRenderer.prototype.resetEditableFeaturesLayer = function() {
	if (! _.isNil(this.editableFeaturesLayer)) {
		this.editableFeaturesLayer.removeFrom(this.editableLayer);
	}

	this.editableFeaturesLayer = Leaflet.geoJSON(
		null,
		{
			style: (feature) => {
				if (this.featuresSelected[feature.id]) {
					return feature.selectedStyle || this.geoJSONSelectedStyle;
				}
				return feature.style || this.geoJSONStyle;
			},

			onEachFeature: (feature, layer) => {
				if (! _.isNil(feature.tooltip)) {
					layer.bindTooltip(
						(layer) => {
							return (_.isFunction(layer.feature.tooltip)
								? layer.feature.tooltip(layer.feature)
								: layer.feature.tooltip);
						},
						layer.feature.tooltipOptions
					);
				}

				layer.on(
					'click',
					(ev) => {
						if (ev.originalEvent.ctrlKey) {
							this.geoJSONFeatureSelectToggle(ev.target.feature);
						}
						else {
							this.userEvent({
								type: 'featureClick',
								feature: feature,
								keyPressed: {
									alt: _.get(ev, 'originalEvent.altKey', false),
									ctrl: _.get(ev, 'originalEvent.ctrlKey', false),
									meta: _.get(ev, 'originalEvent.metaKey', false),
									shift: _.get(ev, 'originalEvent.shiftKey', false)
								},
								location: ev.latlng
							});
						}
					}
				);

				layer.on(
					'contextmenu',
					(ev) => {
						let featuresUnderMouse = leafletPip.pointInLayer(ev.latlng, this.editableFeaturesLayer).map(l => l.feature);

						this.userEvent({
							type: 'featureRightClick',
							feature: feature,
							features: featuresUnderMouse,
							location: ev.latlng,
							keyPressed: {
								alt: _.get(ev, 'originalEvent.altKey', false),
								ctrl: _.get(ev, 'originalEvent.ctrlKey', false),
								meta: _.get(ev, 'originalEvent.metaKey', false),
								shift: _.get(ev, 'originalEvent.shiftKey', false)
							},
							x: ev.originalEvent.pageX,
							y: ev.originalEvent.pageY
						});

						this.openContextMenu('feature', {location: ev.latlng, feature, features: featuresUnderMouse}, ev.originalEvent.pageX, ev.originalEvent.pageY);
					}
				);
			}
		}
	);

	this.editableLayer.addLayer(this.editableFeaturesLayer);
}

LeafletMapViewRenderer.prototype.geoJSONFeatureSelectToggle = function(feature) {
	if (this.featuresSelected[feature.id]) {
		delete this.featuresSelected[feature.id];
	}
	else {
		this.featuresSelected[feature.id] = feature;
	}

	this.geoJSONLayer.resetStyle();

	let selectedFeatures = _.values(this.featuresSelected);

	this.userEvent({
		type: 'selectionChanged',
		geoJSONFeatures: selectedFeatures
	})

	this.requestUpdate({
		'state.selected.features': selectedFeatures
	})
}

LeafletMapViewRenderer.prototype.syncIcons = function(icons) {
	// Updating icons will not affect icons of existing markers (even if the same key is used)
	this.icons = _.mapValues(icons, Leaflet.icon);
}

/* --------------------   Draw paths  ---------------------*/

LeafletMapViewRenderer.prototype.syncDrawPaths = function(drawPaths) {
	// Remove existing
	_.forEach(
		this.drawPaths,
		(path) => {
			if (! path.layer) {
				return
			} 
			path.layer.remove();
		}	
	)

	// Add current state
	this.drawPaths = _.map(
		drawPaths,
		(path) => {
			let layer;

			try {
				layer = Leaflet.curve(
					path.path,
					path.style || {color: '#000000',fill: false}
				).addTo(this.map)
			}
			catch(ex){
				console.error('Draw path is invalid', path);
			}

			return _.extend(
				{},
				path,
				{layer: layer}
			)
		}
	)
}

/* --------------------   Icons  ---------------------*/

LeafletMapViewRenderer.prototype.syncIcons = function(icons) {
	// Updating icons will not affect icons of existing markers (even if the same key is used)
	this.icons = _.mapValues(icons, Leaflet.icon);
}

module.exports = LeafletMapViewRenderer;
